'use client';

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import { tenantFeaturesApi } from '@/lib/api/class-management';

interface TenantCapabilitiesValue {
  /** True until the first GET /v1/me/tenant-features resolution completes. */
  loading: boolean;
  /** True only for a capability key present in the last successful resolution.
   * Fail-closed by construction: loading, logged-out, and error states all
   * resolve to an empty set, so every check returns false — never assume a
   * capability is present. */
  hasCapability: (key: string) => boolean;
}

const TenantCapabilitiesContext = createContext<TenantCapabilitiesValue>({
  loading: true,
  hasCapability: () => false,
});

const EMPTY_CAPABILITIES: ReadonlySet<string> = new Set();

/** A resolution is only ever trusted for the user id it was fetched for. */
type Resolution = { userId: string; capabilities: ReadonlySet<string> };

export function TenantCapabilitiesProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const userId = isAuthenticated ? (user?.id ?? null) : null;
  const [resolution, setResolution] = useState<Resolution | null>(null);

  useEffect(() => {
    if (!userId) return;

    let cancelled = false;
    tenantFeaturesApi
      .get()
      .then((features) => {
        if (!cancelled) setResolution({ userId, capabilities: new Set(features.capabilities) });
      })
      .catch(() => {
        // Fail-closed on any client-side error too (network failure, non-2xx,
        // malformed body) — never grant a capability we couldn't confirm.
        if (!cancelled) setResolution({ userId, capabilities: EMPTY_CAPABILITIES });
      });

    return () => {
      cancelled = true;
    };
  }, [userId]);

  // Derived, never assigned in an effect. Keying on the user id means a logged-out
  // user — or a *different* user signing in before their own fetch resolves — reads
  // as "no capabilities" rather than inheriting the previous session's set.
  const resolved = userId !== null && resolution?.userId === userId ? resolution : null;
  const capabilities = resolved?.capabilities ?? EMPTY_CAPABILITIES;
  const loading = userId !== null && resolved === null;

  const value = useMemo<TenantCapabilitiesValue>(
    () => ({
      loading,
      hasCapability: (key: string) => capabilities.has(key),
    }),
    [loading, capabilities],
  );

  return (
    <TenantCapabilitiesContext.Provider value={value}>
      {children}
    </TenantCapabilitiesContext.Provider>
  );
}

/** Calls GET /v1/me/tenant-features once per session (via context) and
 * exposes the unioned capability set. Fail-closed: loading/logged-out/error
 * all read as "capability absent" — never fail-open. */
export function useTenantCapabilities(): TenantCapabilitiesValue {
  return useContext(TenantCapabilitiesContext);
}
